import { useState, useEffect } from 'react';
import { Building2, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

export default function UnsubscribePage() {
  const [status, setStatus] = useState('confirm'); // confirm, loading, done, error
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') || '';

  const handleUnsubscribe = async () => {
    if (!email) { setStatus('error'); return; }
    setStatus('loading');
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/unsubscribe?email=${encodeURIComponent(email)}`);
      if (res.ok) {
        setStatus('done');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const maskedEmail = email
    ? email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 5)) + c)
    : '';

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col" data-testid="unsubscribe-page">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center">
          <Logo size="md" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-start justify-center pt-16 sm:pt-24 px-4">
        <div className="w-full max-w-md">
          {status === 'confirm' && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8 shadow-sm text-center" data-testid="unsubscribe-confirm">
              <h1 className="text-lg font-bold text-stone-800 mb-2">Unsubscribe from emails</h1>
              <p className="text-sm text-stone-500 mb-1">
                You're about to unsubscribe
              </p>
              {maskedEmail && (
                <p className="text-sm text-stone-700 font-medium mb-6">{maskedEmail}</p>
              )}
              <p className="text-xs text-stone-400 mb-6">
                You will no longer receive product updates, news, or marketing emails from Tenantra Inc.
                You'll still receive important account-related emails.
              </p>
              <Button
                onClick={handleUnsubscribe}
                className="w-full h-10 bg-stone-800 hover:bg-stone-900 text-white text-sm font-medium border-0"
                data-testid="confirm-unsubscribe-btn"
              >
                Unsubscribe
              </Button>
              <a href="/" className="block mt-4 text-xs text-stone-400 hover:text-stone-600 transition-colors">
                Never mind, take me back
              </a>
            </div>
          )}

          {status === 'loading' && (
            <div className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm text-center" data-testid="unsubscribe-loading">
              <Loader2 className="w-6 h-6 text-stone-400 animate-spin mx-auto mb-3" />
              <p className="text-sm text-stone-500">Updating your preferences...</p>
            </div>
          )}

          {status === 'done' && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8 shadow-sm text-center" data-testid="unsubscribe-done">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
              </div>
              <h1 className="text-lg font-bold text-stone-800 mb-2">You've been unsubscribed</h1>
              <p className="text-sm text-stone-500 mb-6">
                You won't receive marketing emails from Tenantra anymore.
                Account-related emails will continue as normal.
              </p>
              <a href="/">
                <Button variant="outline" className="h-9 px-5 text-sm border-stone-200">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Tenantra
                </Button>
              </a>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8 shadow-sm text-center" data-testid="unsubscribe-error">
              <h1 className="text-lg font-bold text-stone-800 mb-2">Something went wrong</h1>
              <p className="text-sm text-stone-500 mb-6">
                We couldn't process your request. Please try again or contact <a href="mailto:support@tenantra.ca" className="text-teal-600 underline">support@tenantra.ca</a>.
              </p>
              <Button onClick={() => setStatus('confirm')} variant="outline" className="h-9 px-5 text-sm border-stone-200">
                Try again
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-4">
        <div className="max-w-xl mx-auto px-4 flex items-center justify-between text-[10px] text-stone-400">
          <span>&copy; {new Date().getFullYear()} Tenantra Inc.</span>
          <div className="flex items-center gap-3">
            <a href="/privacy-policy" className="hover:text-stone-600 transition-colors">Privacy Policy</a>
            <a href="https://tenantra.ca" className="hover:text-stone-600 transition-colors">tenantra.ca</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
